import { logger } from '../../utils/logger';
import React, { useState } from 'react';
import {
    Alert,
    AlertTitle,
    Collapse,
    IconButton,
    Box,
    Typography,
    Paper,
    Button
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

interface ErrorDisplayProps {
    error: any;
    userMessage?: string;
}

// Safe string conversion for any value
const safeStringify = (value: any): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    
    try {
        if (typeof value === 'object') {
            // Handle [object Object] case better
            if (Object.prototype.toString.call(value) === '[object Object]') {
                // Check if it's an empty object
                if (Object.keys(value).length === 0) {
                    return '{}';
                }
                
                // Attempt to extract known error properties
                if (value.message) {
                    return value.message;
                }
                
                if (value.detail) {
                    return typeof value.detail === 'string' 
                        ? value.detail 
                        : JSON.stringify(value.detail, null, 2);
                }
                
                // Otherwise stringify the entire object
                return JSON.stringify(value, null, 2);
            }
            
            // For arrays
            if (Array.isArray(value)) {
                return JSON.stringify(value, null, 2);
            }
            
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    } catch (e) {
        logger.error('Error stringifying value:', e);
        return '[Unparseable Object]';
    }
};

// Format validation errors specifically
const formatValidationErrors = (errors: any[]): string => {
    if (!Array.isArray(errors)) return '';
    
    return errors.map((error, index) => {
        try {
            const location = error.loc && Array.isArray(error.loc) ? error.loc.join('.') : 'unknown';
            const message = error.msg || 'Validation error';
            const type = error.type || '';
            const input = error.input !== undefined ? ` (input: ${safeStringify(error.input)})` : '';
            
            return `${location}: ${message}${type ? ` [${type}]` : ''}${input}`;
        } catch (e) {
            return `Error ${index + 1}: ${safeStringify(error)}`;
        }
    }).join('\n');
};

// Extract user-friendly error message
const getUserMessage = (error: any, userMessage?: string): string => {
    if (userMessage) return userMessage;
    
    // Handle validation errors
    if (error?.validation && Array.isArray(error.validation)) {
        return formatValidationErrors(error.validation);
    }
    
    // Handle response errors
    if (error?.response?.data) {
        const data = error.response.data;
        
        // FastAPI validation errors
        if (data.detail && Array.isArray(data.detail)) {
            return formatValidationErrors(data.detail);
        }
        
        // String error message
        if (typeof data.detail === 'string') {
            return data.detail;
        }
        
        // Object error
        if (typeof data === 'object') {
            return safeStringify(data);
        }
    }
    
    // Handle direct error messages
    if (error?.message) {
        return typeof error.message === 'string' ? error.message : safeStringify(error.message);
    }
    
    // Handle error objects that have loc, msg, etc. properties directly
    if (error && typeof error === 'object' && (error.loc || error.msg || error.type)) {
        try {
            const location = error.loc && Array.isArray(error.loc) ? error.loc.join('.') : (error.loc || 'unknown');
            const message = error.msg || 'Validation error';
            const type = error.type ? ` [${error.type}]` : '';
            const input = error.input !== undefined ? ` (input: ${safeStringify(error.input)})` : '';
            const url = error.url ? ` URL: ${error.url}` : '';
            
            return `${location}: ${message}${type}${input}${url}`;
        } catch (e) {
            return safeStringify(error);
        }
    }
    
    // Handle error objects
    if (error?.detail) {
        return typeof error.detail === 'string' ? error.detail : safeStringify(error.detail);
    }
    
    // Last resort
    return safeStringify(error) || 'An error occurred. Please try again.';
};

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, userMessage }) => {
    const [showDetails, setShowDetails] = useState(false);

    // Debug log the error object
    logger.log('ErrorDisplay received error:', error);
    logger.log('Error type:', typeof error);
    if (typeof error === 'object') {
        logger.log('Error keys:', Object.keys(error));
    }

    const getTechnicalDetails = (error: any) => {
        try {
            const details: any = {
                timestamp: new Date().toISOString(),
                errorType: error?.name || error?.constructor?.name || typeof error
            };

            // Add response details if available
            if (error?.response) {
                details.response = {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    url: error.response.config?.url,
                    method: error.response.config?.method,
                    data: safeStringify(error.response.data)
                };
            }

            // Add request details if available
            if (error?.config) {
                details.request = {
                    url: error.config.url,
                    method: error.config.method,
                    baseURL: error.config.baseURL
                };
            }

            // Add stack trace if available
            if (error?.stack) {
                details.stack = error.stack;
            }

            // Add original error for debugging
            details.originalError = safeStringify(error);

            return details;
        } catch (e) {
            logger.error("Error processing error details:", e);
            return { 
                message: "Error could not be processed",
                originalError: safeStringify(error)
            };
        }
    };

    const formatTechnicalDetails = (details: any) => {
        try {
            return Object.entries(details)
                .map(([key, value]) => {
                    const stringValue = safeStringify(value);
                    return `${key}:\n${stringValue}`;
                })
                .join('\n\n');
        } catch (e) {
            logger.error("Error formatting technical details:", e);
            return "Error details could not be displayed";
        }
    };

    const displayMessage = getUserMessage(error, userMessage);
    const technicalDetails = getTechnicalDetails(error);
    const formattedDetails = formatTechnicalDetails(technicalDetails);

    return (
        <Alert 
            severity="error" 
            sx={{ 
                mb: 2,
                '& .MuiAlert-message': {
                    width: '100%'
                }
            }}
        >
            <AlertTitle>Error</AlertTitle>
            <Box sx={{ mb: 2 }}>
                <Typography 
                    component="pre" 
                    sx={{ 
                        fontFamily: 'inherit',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        margin: 0
                    }}
                >
                    {displayMessage}
                </Typography>
            </Box>
            
            <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setShowDetails(!showDetails)}
                endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                sx={{ mb: 1 }}
            >
                {showDetails ? 'Hide Technical Details' : 'Show Technical Details'}
            </Button>
            
            <Collapse in={showDetails}>
                <Paper 
                    variant="outlined" 
                    sx={{ 
                        mt: 1, 
                        p: 2, 
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        maxHeight: '300px',
                        overflow: 'auto'
                    }}
                >
                    <Typography 
                        component="pre" 
                        sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0
                        }}
                    >
                        {formattedDetails}
                    </Typography>
                </Paper>
            </Collapse>
        </Alert>
    );
}; 